import { useEffect, useRef, useState } from "react";
import { DialogTrigger, Pressable } from "react-aria-components";
import { ChevronDown, ChevronsUpDown, Loader2, MoreHorizontal, Plus, Search, Upload } from "lucide-react";
import { useNavigate } from "react-router";
import { useGetBrands, type Brand } from "@/hooks/useGetBrands";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { cn } from "@/utils/helpers";
import { useCreateBrand } from "@/hooks/useCreateBrand";
import { useDeleteBrand } from "@/hooks/useDeleteBrand";
import { useUploadBrandLogo, BRAND_LOGO_ACCEPT } from "@/hooks/useUploadBrandLogo";
import { usePermissions } from "@/hooks/usePermissions";
import { queue } from "@/components/Toast";
import { getAiRequestErrorMessage } from "@/lib/api";
import { kitDropdownTriggerClass } from "./dropdownStyles";
import { KitPopover } from "./Popover";
import { KitModal } from "./Modal";
import { KitInput } from "./Input";
import { KitButton } from "./Button";

/** Modal used both for the "no brands yet" empty state and the popover's "+ New brand" row. */
function CreateBrandModal({ isOpen, onOpenChange }: { isOpen: boolean; onOpenChange: (open: boolean) => void }) {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const createBrand = useCreateBrand();
  const navigate = useNavigate();

  const handleClose = () => {
    onOpenChange(false);
    setName("");
    setError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Brand name is required");
      return;
    }
    if (trimmed.length > 40) {
      setError("Max 40 characters");
      return;
    }
    createBrand.mutate(trimmed, {
      onSuccess: () => {
        handleClose();
        // New brand is selected in useCreateBrand; empty universe opens onboarding.
        navigate("/dashboard/brand-universe");
      },
      onError: () => setError("Could not create brand"),
    });
  };

  if (!isOpen) return null;

  return (
    <KitModal isOpen onOpenChange={(next) => (next ? onOpenChange(true) : handleClose())} title="Create brand" modalClassName="!w-[min(calc(100vw-2rem),440px)] !max-w-[440px]">
      <form onSubmit={handleSubmit} className="flex w-full min-w-0 flex-col gap-4">
        <KitInput
          label="Brand name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
          placeholder="My brand"
          maxLength={40}
          autoFocus
          error={error ?? undefined}
        />
        <div className="flex gap-3 pt-2">
          <KitButton type="button" variant="ghost" onClick={handleClose} className="flex-1">
            Cancel
          </KitButton>
          <KitButton type="submit" loading={createBrand.isPending} className="flex-1">
            Create
          </KitButton>
        </div>
      </form>
    </KitModal>
  );
}

function DeleteBrandModal({ isOpen, onOpenChange, brandName, onConfirm, isPending }: { isOpen: boolean; onOpenChange: (open: boolean) => void; brandName: string; onConfirm: () => void; isPending: boolean }) {
  if (!isOpen) return null;

  return (
    <KitModal isOpen onOpenChange={onOpenChange} title="Delete brand" modalClassName="!w-[min(calc(100vw-2rem),440px)] !max-w-[440px]">
      <div className="flex w-full min-w-0 flex-col gap-4">
        <p className="text-[14px] leading-relaxed text-co-text2">
          Delete <span className="font-semibold text-co-text">{brandName}</span>? This removes its universe and facts permanently.
        </p>
        <div className="flex gap-3 pt-2">
          <KitButton type="button" variant="ghost" onClick={() => onOpenChange(false)} className="flex-1">
            Cancel
          </KitButton>
          <KitButton type="button" variant="danger" onClick={onConfirm} loading={isPending} className="flex-1">
            {isPending ? "Deleting…" : "Delete"}
          </KitButton>
        </div>
      </div>
    </KitModal>
  );
}

/** Two letters from a brand name, for the monogram tier. Takes the initials of
 *  the first two words, or the first two characters of a single word. */
function monogram(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  const letters = words.length > 1 ? words[0][0] + words[1][0] : words[0].slice(0, 2);
  return letters.toUpperCase();
}

/** The brand mark. One square, three tiers: the logo when it resolves, a
 *  monogram when it does not, a single glyph when the name cannot give two.
 *  A mixed wall is the normal case, so every tier occupies the same box and
 *  the same hairline — a monogram row must never look unfinished beside a
 *  logo row. The logo is contained, never cropped and never tinted. */
const OPEN_BRAND_PICKER = "co:open-brand-picker";

/** Opens the nav rail's brand picker from anywhere (a page's "Choose brand"). */
export function openBrandPicker() {
  window.dispatchEvent(new Event(OPEN_BRAND_PICKER));
}

export function BrandMark({
  brand,
  size = 26,
  /** The nav rail's Brand tile (library 01, `Brand tile`): a hairline, a 20-in-32
   *  logo and the initials in co-text at 14 on 32. Settings keeps the default. */
  tile = false,
}: {
  brand: Brand;
  size?: number;
  tile?: boolean;
}) {
  /* Remembered per URL, not as a flag: the logo is a presigned URL that
     expires, and a flag would keep the monogram up after a refetch or a new
     upload brings a URL that loads. */
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const label = monogram(brand.name);
  const showLogo = !!brand.logoUrl && failedUrl !== brand.logoUrl;
  const logoSize = Math.round(size * (tile ? 0.625 : 0.76));

  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size, padding: showLogo && !tile ? Math.round(size * 0.12) : undefined }}
      /* Brand tile (library, style B, Emad 1 Oct): a flat round disc, the
         logo on a white plate or the brand's two letters; no bevel. */
      className={cn(
        "grid shrink-0 place-items-center overflow-hidden rounded-full",
        showLogo ? "bg-[var(--co-logo-plate,#ffffff)]" : "bg-co-surface-solid",
        tile && "co-brand-mark",
        tile && !showLogo && "co-brand-mark--initials",
      )}
    >
      {showLogo ? (
        /* Plate padding and logo are sized in pixels from `size`. Percentages
           resolve against the PARENT (padding) or an empty grid cell (the img),
           which blew the plate up to the row width with no logo inside. */
        <img
          src={brand.logoUrl as string}
          alt=""
          decoding="async"
          onError={() => setFailedUrl(brand.logoUrl ?? null)}
          style={{ width: logoSize, height: logoSize }}
          className={cn("block max-w-none object-contain", !tile && "rounded-full")}
        />
      ) : tile ? (
        <span
          /* Figma tiles: 42 and 32 carry 14px initials, 28 and 26 carry 11px. */
          style={{ fontSize: size >= 32 ? 14 : 11, letterSpacing: "0.3px" }}
          className="font-section font-extrabold leading-none text-co-text"
        >
          {label}
        </span>
      ) : (
        <span
          style={{ fontSize: Math.max(10, Math.round(size * 0.32)) }}
          className="font-section font-extrabold leading-none tracking-[0.03em] text-co-muted"
        >
          {label}
        </span>
      )}
    </span>
  );
}

/** Cockpit-styled brand switcher — same data source as the old shell's BrandPicker (useGetBrands), plus create/delete (previously separate CreateBrandButton/DeleteBrandButton). */
export function KitBrandPicker({
  className,
  compact,
  variant = "default",
  folded = false,
}: {
  className?: string;
  compact?: boolean;
  variant?: "default" | "header";
  /** Rail is heading to the icon strip. The header disc grows while it folds. */
  folded?: boolean;
}) {
  const { data: brands, isLoading } = useGetBrands();
  const { selectedBrandId, setSelectedBrandId } = useWorkspaceStore();
  const { isOwner, canEdit } = usePermissions();
  const [createOpen, setCreateOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const triggerRef = useRef<HTMLButtonElement>(null);
  /* Figma 11: folded, the panel sits 10 off the rail (82), 2 under the tile's
     top; open (rail or drawer), 8 right of the brand row, kept 12 inside the
     screen. Measured when it opens. */
  const [offset, setOffset] = useState(30);
  const placeFor = () => {
    const t = triggerRef.current?.getBoundingClientRect();
    if (!t) return;
    if (compact) {
      const rail = triggerRef.current?.closest("#co-nav-rail")?.getBoundingClientRect();
      setOffset((rail ? rail.right - 1 + 10 : t.right + 30) - t.right);
      return;
    }
    const row = triggerRef.current?.parentElement?.parentElement?.getBoundingClientRect();
    const left = Math.min((row?.right ?? t.right) + 8, window.innerWidth - 12 - 320);
    setOffset(left - t.right);
  };

  useEffect(() => {
    const open = () => {
      placeFor();
      setMenuOpen(true);
    };
    window.addEventListener(OPEN_BRAND_PICKER, open);
    return () => window.removeEventListener(OPEN_BRAND_PICKER, open);
  });
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  /** Which row has revealed its overflow. A destructive action never sits
   *  inside the target you are aiming at, so delete lives behind this. */
  const [overflowId, setOverflowId] = useState<string | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const deleteActionRef = useRef<HTMLButtonElement>(null);
  const moreButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const logoInputRef = useRef<HTMLInputElement>(null);
  const deleteBrand = useDeleteBrand();
  const uploadLogo = useUploadBrandLogo(selectedBrandId);

  useEffect(() => {
    if (!overflowId) return;
    deleteActionRef.current?.focus();
  }, [overflowId]);

  useEffect(() => {
    if (!brands?.length) return;
    const exists = brands.some((b) => b.id === selectedBrandId);
    if (!selectedBrandId || !exists) {
      setSelectedBrandId(brands[0].id);
    }
  }, [brands, selectedBrandId, setSelectedBrandId]);

  if (isLoading) {
    return compact ? null : <span className="text-[13px] font-medium text-co-faint">Loading brands…</span>;
  }

  if (!brands?.length) {
    if (!isOwner) {
      return compact ? null : <span className="co-rail-text whitespace-nowrap text-[13px] font-medium text-co-faint">No brands yet</span>;
    }
    return (
      <>
        {compact ? (
          <button
            type="button"
            aria-label="Create brand"
            title="Create brand"
            onClick={() => setCreateOpen(true)}
            className={cn("co-brand-tile mx-auto shrink-0", className)}
          >
            <span className="co-brand-mark co-brand-mark--initials grid size-8 place-items-center rounded-full bg-co-surface-solid text-co-text">
              <Plus className="size-4" />
            </span>
          </button>
        ) : (
          <KitButton type="button" iconLeft={Plus} onClick={() => setCreateOpen(true)} className={cn("w-full", className)}>
            Create brand
          </KitButton>
        )}
        <CreateBrandModal isOpen={createOpen} onOpenChange={setCreateOpen} />
      </>
    );
  }

  const selected = brands.find((b) => b.id === selectedBrandId) ?? brands[0];
  const deleteTarget = deleteTargetId ? brands.find((b) => b.id === deleteTargetId) : null;
  const otherBrands = brands.filter((brand) => brand.id !== selected.id);

  const q = query.trim().toLowerCase();
  const shown = q ? otherBrands.filter((b) => b.name.toLowerCase().includes(q)) : otherBrands;
  const showSearch = otherBrands.length > 5;

  const pick = (id: string) => {
    setSelectedBrandId(id);
    setMenuOpen(false);
  };

  const onLogoPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Cleared so picking the same file again still fires a change.
    e.target.value = "";
    if (!file) return;
    uploadLogo.mutate(file, {
      onError: (error) =>
        queue.add({
          title: "Couldn't upload the logo",
          description: getAiRequestErrorMessage(error, "Please try again."),
          variant: "error",
        }),
    });
  };

  const focusBrandAt = (index: number) => {
    const target = listRef.current?.querySelectorAll<HTMLButtonElement>("[data-brand-pick]")[index];
    target?.focus();
    target?.scrollIntoView({ block: "nearest" });
  };

  /** Arrows move, Enter switches, Escape closes. The switcher is the most-used
   *  control after nav, so it never needs the mouse. */
  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!shown.length) return;
      const next = e.key === "ArrowDown"
        ? Math.min(cursor + 1, shown.length - 1)
        : Math.max(cursor - 1, 0);
      setCursor(next);
      focusBrandAt(next);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = shown[cursor];
      if (target) pick(target.id);
    }
  };

  const onBrandKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();

    if (e.key === "ArrowUp" && index === 0 && showSearch) {
      searchRef.current?.focus();
      return;
    }

    const next = e.key === "ArrowDown"
      ? Math.min(index + 1, shown.length - 1)
      : Math.max(index - 1, 0);
    setCursor(next);
    focusBrandAt(next);
  };

  return (
    <>
      <DialogTrigger
        isOpen={menuOpen}
        onOpenChange={(open) => {
          setMenuOpen(open);
          if (open) {
            placeFor();
            setCursor(showSearch ? -1 : 0);
          }
          // Reset here rather than in an effect: closing is the event, so the
          // state settles in one render instead of cascading.
          if (!open) {
            setQuery("");
            setCursor(0);
            setOverflowId(null);
          }
        }}
      >
        <Pressable>
          {compact && variant !== "header" ? (
            /* Brand tile (library 01, 34:38623): the 32 disc itself is the
               control; hover, press, focus and open are drawn in cockpit.css. */
            <button
              ref={triggerRef}
              type="button"
              aria-label={`Switch brand (currently ${selected.name})`}
              aria-expanded={menuOpen}
              title={selected.name}
              className={cn("co-brand-tile mx-auto shrink-0", className)}
            >
              <BrandMark key={selected.id} brand={selected} size={32} tile />
            </button>
          ) : variant === "header" ? (
            /* One disc for both rail states. Folded it is 32; open it is 26,
               and it grows between them instead of swapping. */
            <button
              ref={triggerRef}
              type="button"
              aria-label={`Switch brand (currently ${selected.name})`}
              aria-expanded={menuOpen}
              title={compact ? selected.name : undefined}
              data-folded={folded ? "true" : undefined}
              className={cn(
                "co-brand-rail-trigger relative flex h-9 w-full min-w-0 cursor-pointer items-center gap-2 rounded-[12px] border-0 bg-transparent text-left outline-none",
                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-text",
                className,
              )}
            >
              {compact ? null : (
                <span aria-hidden="true" className="co-glass-control co-rail-text pointer-events-none absolute inset-0 rounded-[12px]" />
              )}
              <span className="co-brand-rail-mark" data-folded={folded ? "true" : undefined}>
                <BrandMark key={selected.id} brand={selected} size={32} tile />
              </span>
              {compact ? null : (
                <>
                  <span className="co-rail-text relative min-w-0 flex-1 truncate font-body text-[13px] font-semibold text-co-text">
                    {selected.name}
                  </span>
                  <ChevronsUpDown className="co-rail-text relative size-3.5 shrink-0 text-co-muted" />
                </>
              )}
            </button>
          ) : (
            <button
              ref={triggerRef}
              type="button"
              aria-label={`Switch brand (currently ${selected.name})`}
              aria-expanded={menuOpen}
              className={cn(kitDropdownTriggerClass, "h-9 w-full justify-between gap-2 px-3 pe-2", className)}
            >
              <BrandMark key={selected.id} brand={selected} size={20} />
              <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-co-text">{selected.name}</span>
              <ChevronDown className="size-3.5 shrink-0 text-co-faint" />
            </button>
          )}
        </Pressable>

        <KitPopover
          placement={variant === "default" && !compact ? "bottom start" : "right top"}
          offset={variant === "default" && !compact ? 10 : offset}
          crossOffset={compact ? 2 : 0}
          shouldFlip={variant === "default" && !compact}
          containerPadding={12}
          className="co-stagger co-chrome-sheet co-brand-switcher w-[320px] max-w-[calc(100vw-24px)] p-0"
        >
          <div className="co-brand-switcher__head">
            <div className="co-brand-switcher__meta">
              <span>Brands</span>
              <span>{String(brands.length).padStart(2, "0")} total</span>
            </div>

            <div className="co-brand-switcher__current">
              {canEdit ? (
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadLogo.isPending}
                  aria-busy={uploadLogo.isPending}
                  aria-label={
                    uploadLogo.isPending
                      ? "Uploading your brand logo"
                      : selected.logoUrl
                        ? "Change your brand logo"
                        : "Upload your brand logo"
                  }
                  title={selected.logoUrl ? "Change your brand logo" : "Upload your brand logo"}
                  className="co-brand-switcher__logo-upload"
                >
                  <BrandMark key={selected.id} brand={selected} size={42} tile />
                  <span className="co-brand-switcher__logo-upload-overlay" aria-hidden="true">
                    {uploadLogo.isPending ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <Upload className="size-3.5" />
                    )}
                  </span>
                </button>
              ) : (
                <BrandMark key={selected.id} brand={selected} size={42} tile />
              )}
              <div className="min-w-0 flex-1">
                <p className="co-brand-switcher__name">{selected.name}</p>
                <p className="co-brand-switcher__sub">Current brand</p>
              </div>
              {isOwner ? (
                overflowId === selected.id ? (
                  <span id={`brand-actions-${selected.id}`} className="co-brand-switcher__row-actions">
                    <button
                      ref={deleteActionRef}
                      type="button"
                      onClick={() => {
                        setOverflowId(null);
                        setDeleteTargetId(selected.id);
                      }}
                      className="co-brand-switcher__delete"
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setOverflowId(null);
                        requestAnimationFrame(() => moreButtonRefs.current.get(selected.id)?.focus());
                      }}
                      className="co-brand-switcher__cancel"
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    ref={(node) => {
                      if (node) moreButtonRefs.current.set(selected.id, node);
                      else moreButtonRefs.current.delete(selected.id);
                    }}
                    type="button"
                    aria-label={`More actions for ${selected.name}`}
                    aria-expanded={false}
                    aria-controls={`brand-actions-${selected.id}`}
                    onClick={() => setOverflowId(selected.id)}
                    className="co-brand-switcher__more co-brand-switcher__more--current"
                  >
                    <MoreHorizontal className="size-4" />
                  </button>
                )
              ) : null}
            </div>

            {canEdit ? (
              <input
                ref={logoInputRef}
                type="file"
                accept={BRAND_LOGO_ACCEPT}
                onChange={onLogoPick}
                style={{ display: "none" }}
              />
            ) : null}
          </div>

          {showSearch ? (
            <div className="co-brand-switcher__search-wrap">
              <div className="co-brand-switcher__search">
                <Search className="size-3.5 shrink-0 text-co-faint" aria-hidden="true" />
                <input
                  ref={searchRef}
                  autoFocus
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setCursor(-1);
                  }}
                  onKeyDown={onSearchKeyDown}
                  placeholder="Find a brand"
                  aria-label="Search brands"
                  className="co-input-own-focus min-w-0 flex-1 border-0 bg-transparent text-[13px] text-co-text outline-none shadow-none placeholder:text-co-faint"
                />
                <span className="co-brand-switcher__keys">↑↓ ⏎</span>
              </div>
            </div>
          ) : null}

          <div className="co-brand-switcher__list-head">
            <span>Switch to</span>
            {q ? <span>{shown.length} found</span> : null}
          </div>

          {shown.length === 0 && !isOwner ? (
            <p className="co-brand-switcher__empty">
              {q ? "No brand matches that. Try fewer letters." : "No other brands yet."}
            </p>
          ) : (
            <ul ref={listRef} className="co-brand-switcher__list co-stagger-rows">
              {shown.length === 0 && q ? (
                <li className="co-brand-switcher__empty">No brand matches that. Try fewer letters.</li>
              ) : null}
              {shown.map((brand, i) => {
                const isCursor = i === cursor;
                return (
                  <li
                    key={brand.id}
                    className="co-brand-switcher__row"
                    data-cursor={isCursor || undefined}
                  >
                    <button
                      type="button"
                      onClick={() => pick(brand.id)}
                      onMouseMove={() => setCursor(i)}
                      onKeyDown={(e) => onBrandKeyDown(e, i)}
                      autoFocus={!showSearch && i === 0}
                      data-brand-pick
                      className="co-brand-switcher__pick"
                    >
                      <BrandMark brand={brand} size={28} tile />
                      <span className="min-w-0 flex-1 truncate">{brand.name}</span>
                    </button>
                  </li>
                );
              })}
              {/* New brand closes the list (Figma 11, Nav / Brand picker). */}
              {isOwner ? (
                <li className="co-brand-switcher__row">
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setCreateOpen(true);
                    }}
                    className="co-brand-switcher__pick"
                  >
                    <span className="co-brand-switcher__create-icon" aria-hidden="true">
                      <Plus className="size-3.5" />
                    </span>
                    <span className="min-w-0 flex-1 truncate">New brand</span>
                  </button>
                </li>
              ) : null}
            </ul>
          )}
        </KitPopover>

      </DialogTrigger>

      <CreateBrandModal isOpen={createOpen} onOpenChange={setCreateOpen} />
      {deleteTarget && (
        <DeleteBrandModal
          isOpen
          onOpenChange={(next) => {
            if (next) return;
            const targetId = deleteTarget.id;
            setDeleteTargetId(null);
            requestAnimationFrame(() => moreButtonRefs.current.get(targetId)?.focus());
          }}
          brandName={deleteTarget.name}
          isPending={deleteBrand.isPending}
          onConfirm={() =>
            deleteBrand.mutate(deleteTarget.id, {
              onSuccess: () => setDeleteTargetId(null),
            })
          }
        />
      )}
    </>
  );
}
